import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const CHIP = {
  uploading: ["Uploading", "bg-sky-50 text-sky-700 border-sky-200"],
  saved: ["Saved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  extracting: ["Extracting", "bg-violet-50 text-violet-700 border-violet-200 animate-pulse"],
  needs_review: ["Needs review", "bg-amber-50 text-amber-800 border-amber-300"],
  complete: ["Complete", "bg-teal-50 text-teal-800 border-teal-200"],
  failed: ["Failed", "bg-rose-50 text-rose-700 border-rose-200"],
  local: ["Saved on this device", "bg-orange-50 text-orange-700 border-orange-200"],
  new: ["New", "bg-sky-50 text-sky-700 border-sky-200"],
  ready_to_contact: ["Ready to contact", "bg-teal-50 text-teal-800 border-teal-200"],
  draft_ready: ["Draft ready", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  contacted: ["Contacted", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  replied: ["Replied", "bg-green-100 text-green-800 border-green-300"],
  follow_up_due: ["Follow-up due", "bg-amber-50 text-amber-800 border-amber-300"],
  closed: ["Closed", "bg-zinc-100 text-zinc-600 border-zinc-200"],
  do_not_contact: ["Do not contact", "bg-rose-50 text-rose-700 border-rose-200"],
  draft: ["Draft", "bg-slate-100 text-slate-700 border-slate-200"],
  pending: ["Pending", "bg-slate-100 text-slate-700 border-slate-300"],
  scheduled: ["Scheduled", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  sending: ["Sending", "bg-cyan-50 text-cyan-700 border-cyan-200 animate-pulse"],
  accepted: ["Provider accepted", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  delivered: ["Delivered", "bg-green-100 text-green-800 border-green-300"],
  uncertain: ["Outcome uncertain", "bg-amber-50 text-amber-800 border-amber-300"],
  cancelled: ["Cancelled", "bg-zinc-100 text-zinc-600 border-zinc-200"],
  paused: ["Paused", "bg-amber-50 text-amber-800 border-amber-300"],
  completed: ["Completed", "bg-teal-50 text-teal-800 border-teal-200"],
  active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  missing: ["Setup needed", "bg-rose-50 text-rose-700 border-rose-200"],
  not_configured: ["Not configured", "bg-slate-100 text-slate-600 border-slate-200"],
  error: ["Error", "bg-rose-50 text-rose-700 border-rose-200"],
};

export const statusLabel = (s) => CHIP[s]?.[0] || s;

export function StatusChip({ status, testid }) {
  const [label, cls] = CHIP[status] || [status, "bg-slate-100 text-slate-700 border-slate-200"];
  return (
    <span data-testid={testid || `status-${status}`} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {(status === "extracting" || status === "sending" || status === "uploading") && <Loader2 className="h-3 w-3 animate-spin" />}
      {label}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between cd-fade">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Empty({ icon: Icon, title, text, action }) {
  return (
    <div className="cd-card flex flex-col items-center py-10 text-center" data-testid="empty-state">
      {Icon && <Icon className="mb-3 h-8 w-8 text-slate-300" />}
      <p className="font-semibold text-slate-800">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-slate-500">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Field({ label, children, hint, warn }) {
  return (
    <label className="block">
      <span className="cd-label">{label}</span>
      {children}
      {hint && <span className={`mt-1 block text-xs ${warn ? "text-amber-700" : "text-slate-500"}`}>{hint}</span>}
    </label>
  );
}

export function Select({ value, onChange, options, placeholder, testid, className = "" }) {
  return (
    <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`cd-input ${className}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const [v, l] = Array.isArray(o) ? o : [o, o];
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

export function SecureImage({ fileId, alt, className, testid }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/files/${fileId}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setErr(true));
    return () => url && URL.revokeObjectURL(url);
  }, [fileId]);
  if (err) return <div className={`flex items-center justify-center bg-slate-100 text-xs text-slate-400 ${className}`}>Image unavailable</div>;
  if (!src) return <div className={`min-h-40 animate-pulse bg-slate-100 ${className}`} />;
  return <img data-testid={testid} src={src} alt={alt || "source"} className={className} />;
}

export function Modal({ open, onClose, title, children, wide, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-900/40 backdrop-blur-sm sm:items-center" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()}
        className={`cd-fade max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-xl sm:rounded-3xl ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">{title}</h3>
          <button data-testid="modal-close-btn" onClick={onClose} className="rounded-full p-2 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
export const displayName = (c) => c?.full_name || c?.company || c?.emails?.[0]?.value || "Unnamed contact";

export function usePoll(fn, ms, deps = []) {
  useEffect(() => {
    fn();
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
